import { useState } from "react";
import { CheckCircle2, XCircle, Download, Send, Loader2, Eye, MailOpen } from "lucide-react";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { toast } from "sonner";
import api from "@/lib/api";
import { timeAgo } from "@/lib/timeAgo";

export const EngagementBadge = ({ item, testId }) => {
  const views = item.view_count || 0;
  const opens = item.email_opens || 0;
  if (!views && !opens) return <span className="text-xs text-slate-400" data-testid={testId}>Not opened yet</span>;
  return (
    <span className="inline-flex flex-wrap gap-1.5" data-testid={testId}>
      {views > 0 && <span className="text-[11px] font-bold bg-amber-100 text-amber-800 px-2 py-0.5 rounded-full flex items-center gap-1"><Eye className="w-3 h-3" />Viewed {views}× · {timeAgo(item.last_viewed_at)}</span>}
      {opens > 0 && <span className="text-[11px] font-medium bg-sky-50 text-sky-700 px-2 py-0.5 rounded-full flex items-center gap-1"><MailOpen className="w-3 h-3" />Email opened {opens}×</span>}
    </span>
  );
};

export const PLAN_STATUS_STYLES = {
  draft: "bg-slate-100 text-slate-700",
  approved: "bg-green-100 text-green-700",
  rejected: "bg-red-100 text-red-700",
  generating: "bg-blue-100 text-blue-700",
  failed: "bg-red-100 text-red-700",
};

const errMsg = (err, fallback) => {
  const d = err.response?.data?.detail;
  return Array.isArray(d) ? d.map(e => e.msg).join(", ") : d || fallback;
};

const RESPONSE_LABELS = {
  accepted: ["Accepted · deposit paid", "bg-green-600 text-white"],
  changes_requested: ["Changes requested", "bg-amber-100 text-amber-800"],
  declined: ["Declined", "bg-slate-200 text-slate-700"],
};

const ProspectResponseBadge = ({ resp }) => {
  const [label, cls] = RESPONSE_LABELS[resp.action] || [resp.action, "bg-slate-100"];
  return (
    <div className="w-full flex flex-wrap items-start gap-2" data-testid="plan-prospect-response">
      <span className={`text-[11px] font-bold uppercase px-2 py-0.5 rounded-full ${cls}`}>{label}</span>
      <span className="text-xs text-slate-500">{resp.name ? `${resp.name} · ` : ""}{timeAgo(resp.at)}</span>
      {resp.note && <p className="w-full text-sm text-slate-700 bg-slate-50 border border-slate-200 rounded-lg p-2.5 italic" data-testid="plan-prospect-note">“{resp.note}”</p>}
    </div>
  );
};

const SendDialog = ({ open, onOpenChange, plan, audit, onSent }) => {
  const [email, setEmail] = useState(audit?.email || "");
  const [busy, setBusy] = useState(false);
  const send = async () => {
    setBusy(true);
    try {
      if (email.trim().toLowerCase() !== (audit?.email || "")) await api.put(`/api/webadvisor/audit/${audit.id}/prospect-email`, { email });
      const { data } = await api.post(`/api/webadvisor/plan/${plan.id}/send`, { origin_url: window.location.origin });
      toast.success(data.message); onSent(data.sent_at, data.sent_to); onOpenChange(false);
    } catch (err) { toast.error(errMsg(err, "Send failed")); }
    finally { setBusy(false); }
  };
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="bg-white" data-testid="send-proposal-dialog">
        <DialogHeader><DialogTitle>Email proposal to prospect</DialogTitle></DialogHeader>
        <div className="space-y-3">
          <div><Label className="text-sm">Prospect email</Label><Input type="email" value={email} onChange={e => setEmail(e.target.value)} placeholder="owner@theirshop.com" data-testid="send-proposal-email" /></div>
          <p className="text-xs text-slate-500">The email is saved to this lead. The prospect gets a branded email with a "View your proposal" button, where they can download the PDF and accept the plan. Replies come to you.</p>
          <button onClick={send} disabled={busy || !email.trim()} className="w-full bg-blue-600 hover:bg-blue-500 text-white font-bold py-2.5 rounded-lg disabled:opacity-50 flex items-center justify-center gap-2" data-testid="send-proposal-submit">
            {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : <Send className="w-4 h-4" />}Send Proposal
          </button>
        </div>
      </DialogContent>
    </Dialog>
  );
};

export const PlanActions = ({ plan, audit, onPlanChange }) => {
  const [busy, setBusy] = useState("");
  const [sendOpen, setSendOpen] = useState(false);

  const decide = async (approved) => {
    setBusy(approved ? "approve" : "reject");
    try {
      const { data } = await api.post(`/api/webadvisor/plan/${plan.id}/approve`, { approved, notes: "" });
      onPlanChange(data); toast.success(approved ? "Plan approved" : "Plan rejected");
    } catch (err) { toast.error(errMsg(err, "Update failed")); }
    finally { setBusy(""); }
  };

  const download = async () => {
    setBusy("pdf");
    try {
      const res = await api.get(`/api/webadvisor/plan/${plan.id}/pdf`, { responseType: "blob" });
      const name = res.headers["content-disposition"]?.match(/filename="(.+)"/)?.[1] || "Growth-Proposal.pdf";
      const url = URL.createObjectURL(res.data);
      const a = document.createElement("a"); a.href = url; a.download = name; a.click();
      URL.revokeObjectURL(url);
    } catch { toast.error("Could not build the PDF"); }
    finally { setBusy(""); }
  };

  const approved = plan.status === "approved";
  return (
    <div className="bg-white border border-slate-200 rounded-xl p-4 shadow-sm flex flex-wrap items-center gap-3" data-testid="plan-actions">
      <span className={`text-xs font-bold uppercase tracking-wide px-2.5 py-1 rounded-full ${PLAN_STATUS_STYLES[plan.status] || PLAN_STATUS_STYLES.draft}`} data-testid="plan-status-badge">{plan.status}</span>
      {plan.sent_at && <span className="text-xs text-slate-500" data-testid="plan-sent-info">Sent to {plan.sent_to} · {plan.sent_at.slice(0, 10)}</span>}
      {plan.sent_at && <EngagementBadge item={plan} testId="plan-engagement" />}
      {plan.prospect_response && <ProspectResponseBadge resp={plan.prospect_response} />}
      <div className="flex flex-wrap gap-2 ml-auto">
        {plan.status !== "approved" && (
          <button onClick={() => decide(true)} disabled={!!busy} className="bg-green-600 hover:bg-green-500 text-white text-sm font-medium px-3 py-2 rounded-lg flex items-center gap-1.5 disabled:opacity-50" data-testid="plan-approve-btn">
            {busy === "approve" ? <Loader2 className="w-4 h-4 animate-spin" /> : <CheckCircle2 className="w-4 h-4" />}Approve
          </button>
        )}
        {plan.status !== "rejected" && plan.prospect_response?.action !== "accepted" && (
          <button onClick={() => decide(false)} disabled={!!busy} className="border border-red-200 text-red-600 hover:bg-red-50 text-sm font-medium px-3 py-2 rounded-lg flex items-center gap-1.5 disabled:opacity-50" data-testid="plan-reject-btn">
            {busy === "reject" ? <Loader2 className="w-4 h-4 animate-spin" /> : <XCircle className="w-4 h-4" />}Reject
          </button>
        )}
        <button onClick={download} disabled={!!busy} className="border border-slate-300 hover:bg-slate-50 text-sm font-medium px-3 py-2 rounded-lg flex items-center gap-1.5 disabled:opacity-50" data-testid="plan-download-pdf-btn">
          {busy === "pdf" ? <Loader2 className="w-4 h-4 animate-spin" /> : <Download className="w-4 h-4" />}Download PDF
        </button>
        <button onClick={() => setSendOpen(true)} disabled={!approved || !!busy} title={approved ? "" : "Approve the plan first"} className="bg-blue-600 hover:bg-blue-500 text-white text-sm font-medium px-3 py-2 rounded-lg flex items-center gap-1.5 disabled:opacity-50" data-testid="plan-send-btn">
          <Send className="w-4 h-4" />Email to Prospect
        </button>
      </div>
      {!approved && <p className="w-full text-xs text-slate-400">Approve the plan to email it to the prospect.</p>}
      {sendOpen && <SendDialog open={sendOpen} onOpenChange={setSendOpen} plan={plan} audit={audit} onSent={(sent_at, sent_to) => onPlanChange({ ...plan, sent_at, sent_to })} />}
    </div>
  );
};
